import { MonthYearInput } from '@adornis/formfield/fields/month-year-input.js';
import type { FunctionalForm } from '@adornis/forms/use-form.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { formatCalendarDay } from '@adornis/datetime/calendarDay.js';
import { FormGridEntry } from '@martinus/styles/client/FormGridEntry.js';
import { html, nothing } from 'lit';
import { canonicalEndOfPreviousMonth, shiftCanonicalMonths, toPlainDate } from '../../../db/utils/jobTicketDates.js';
import type { JobTicketFormData } from '../../../db/utils/jobTicketValidator.js';

/**
 * Renders the period the ticket is valid for. The stored cancel date is the month *after* the last
 * valid one, so the period ends with the last day of the month before it.
 */
const ValidityDisplay = (props: { form: FunctionalForm<JobTicketFormData> }) => {
  const orderDate = toPlainDate(props.form.document.jobTicketBestellDatum);
  if (!orderDate) return nothing;

  const cancelDate = toPlainDate(props.form.document.jobTicketKuendigungsDatum);

  return html`
    <p class="col-span-full text-sm text-base-content/70">
      Ihr Ticket ist damit vom ${formatCalendarDay(orderDate, 'dd.MM.yyyy')}
      ${cancelDate ? `bis zum ${formatCalendarDay(canonicalEndOfPreviousMonth(cancelDate), 'dd.MM.yyyy')}` : ''} gültig.
    </p>
  `;
};

/**
 * Order and cancellation month of the ticket. Both are month granular; the cancellation is picked as
 * the last valid month while the stored value is the month after it (see `../../../db/utils/jobTicketDates.js`).
 */
export const JobTicketValidityFields = stateful((props: { form: FunctionalForm<JobTicketFormData> }) => {
  const orderField = props.form.field('jobTicketBestellDatum');
  const cancelField = props.form.field('jobTicketKuendigungsDatum');
  // the picker works on plain dates - the form document hands out proxied ones
  const orderValue = toPlainDate(orderField.value);
  const cancelValue = toPlainDate(cancelField.value);

  return html`
    <div class="form-grid">
      ${FormGridEntry({
        label: 'Gültig ab',
        render: MonthYearInput({
          value: orderValue,
          disabled: orderField.disabled,
          error: orderField.error,
          clearable: true,
          monthPlaceholder: 'Ab Anfang des Monats',
          yearPlaceholder: 'Ab Jahr',
          onPicked: value => orderField.onPicked?.(value),
        }),
      })}
      ${FormGridEntry({
        label: 'Gültig bis',
        render: MonthYearInput({
          // the user picks the last valid month, stored is the month after it
          value: cancelValue ? shiftCanonicalMonths(cancelValue, -1) : null,
          disabled: cancelField.disabled,
          error: cancelField.error,
          clearable: true,
          monthPlaceholder: 'Bis Ende des Monats',
          yearPlaceholder: 'Bis Jahr',
          onPicked: value => cancelField.onPicked?.(value ? shiftCanonicalMonths(value, 1) : null),
        }),
      })}
      <p class="col-span-full text-sm text-base-content/70">
        *Hinweis: Sie können das Ticket nur bis zum 9. (inklusive) des Vormonats bestellen und kündigen.
      </p>
      ${ValidityDisplay({ form: props.form })}
    </div>
  `;
});
